import { useTranslation } from "react-i18next";
import { InfoPopover } from "./InfoPopover";
import { type Api } from "../../../services/api";
import { studyDesignLabel } from "../../../domains/app/utils/getArticleTypeText";
import { type RequestOutput } from "../../../../../api/src/shared/request";

// The Work's methodological identity, inferred from the read endpoint's contract
// (never a hand-written shape): its study design and which standardized appraisal
// grid applies. The wire carries nothing else. The endpoint projects the stored
// appraisal down to this identity, so a bias verdict ("high/low risk") is not a
// field this component could read even by mistake — a property of the endpoint,
// not a rule about the surface. The citation sheet states the verdict in full
// through `StudyAppraisalLedger`, and stands this thin readout down when it
// does; see the note on that endpoint for why PRD §4's older reading retired.
export type WorkMethodView = RequestOutput<
  Api["get"]["/webapp/private/works/risk-of-bias"]
>["body"][number]["method"];

type AppraisedMethod = Extract<
  NonNullable<WorkMethodView>,
  { state: "appraised" }
>;
type Design = AppraisedMethod["design"];
type Instrument = AppraisedMethod["instrument"];

// The appraisal grid is stored as a machine code; show its established name
// (proper appraisal-tool names, not translated). Exhaustive by construction.
const INSTRUMENT_LABEL = {
  rob2: "RoB 2",
  robins_i: "ROBINS-I",
  nos: "Newcastle–Ottawa Scale",
  jbi_cross_sectional: "JBI Checklist",
  quadas2: "QUADAS-2",
  robis: "ROBIS",
  casp_qualitative: "CASP Qualitative",
  quips: "QUIPS",
} as const satisfies Record<Instrument, string>;

// The words come from the shared table, so this readout and the general chat's
// sources list name a design identically. `t` is passed in rather than read from
// the singleton, so a language change re-renders the label.
function useDesignLabel(): (design: Design) => string {
  const { t } = useTranslation();
  return (design) => studyDesignLabel(t, design);
}

// Neutral chip look — the readout is an identity, not a grade, so no colour scale.
const CHIP_CLASS =
  "inline-flex w-fit items-center rounded-full border border-ds-slate-200 bg-ds-slate-50 px-2 py-0.5 text-xs font-medium text-ds-slate-700";

// The compact chip next to a source in the answer's source list: the study's
// methodological identity (its design), or "no grid applies" for a study type no
// instrument covers (editorial, protocol…). Renders nothing until a result is in,
// so the list stays clean while the read is in flight or a source has none.
export function SourceMethodBadge({ method }: { method: WorkMethodView }) {
  const { t } = useTranslation();
  const designLabel = useDesignLabel();
  if (method === null) return null;
  if (method.state === "not_appraisable")
    return <span className={CHIP_CLASS}>{t("No appraisal grid applies")}</span>;
  return <span className={CHIP_CLASS}>{designLabel(method.design)}</span>;
}

// The full methodological readout for the citation sheet: the study design and
// the applicable appraisal grid — the "which grid applies" the PRD asks for, and
// nothing more. No risk level, no per-domain verdict. Handles all three states so
// the sheet never reads an absent field.
export function SourceMethodReadout({ method }: { method: WorkMethodView }) {
  const { t } = useTranslation();
  const designLabel = useDesignLabel();

  const header = (
    <span className="flex items-center gap-1 text-xs font-semibold text-ds-slate-600">
      {t("Method")}
      <InfoPopover label={t("Method")} align="left">
        {t(
          "The study design determines which standardized appraisal grid applies. Juisci shows the method used to weigh the evidence, not a verdict on the study."
        )}
      </InfoPopover>
    </span>
  );

  if (method === null) {
    return (
      <div className="flex flex-col gap-2">
        {header}
        <span className="text-sm text-ds-slate-400">
          {t("Not yet analysed.")}
        </span>
      </div>
    );
  }

  if (method.state === "not_appraisable") {
    return (
      <div className="flex flex-col gap-2">
        {header}
        <span className="text-sm text-ds-slate-500">
          {t("No appraisal grid applies for this study type.")}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {header}
      <div className="flex flex-wrap items-center gap-2">
        <span className={CHIP_CLASS}>{designLabel(method.design)}</span>
        <span className="text-xs text-ds-slate-500">
          {t("Appraisal grid: {{instrument}}", {
            instrument: INSTRUMENT_LABEL[method.instrument],
          })}
        </span>
      </div>
    </div>
  );
}
